import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
} from '@mui/material'
import { useEffect } from 'react'
import { useSnackbar } from '@/hooks/useSnackbar'
import { useLazyPrintPayConfirmQuery } from '@/store/api/procurementApi'
import { printAndUploadReport } from '@/utils/printAndUploadReport'
import { buildPrintPreviewHtml, sanitizePdfFileNamePart } from '@/utils/printPreview'
import { HIDE_ON_MOBILE_INLINE, PRINT_PREVIEW_DIALOG_PAPER, TEXT_TRUNCATE } from '@/theme/responsive'

// Access port: btnXNTTver2 "Print - XNTT" → rpProcurementPayConfirm_* (Pay Confirm).
// BE chọn variant theo row (pay_data_confirm_print). Self-fetching dialog: lazy-fires
// the print query on open, renders returned `html` into an iframe srcDoc + Print button.
// Mirrors ProcurementLiquidPrintDialog.

interface Props {
  open: boolean
  /** Account chỉ View vẫn xem Preview, nhưng ẩn nút Print (in thật). */
  canEdit?: boolean
  paydataId: number | null
  xnttNo?: string | null
  /** payment_type của record — phần cuối tên file. */
  paymentType?: string | null
  /** contract_no của record — prefix tên file "<contract no>_<xntt no>_<payment type>.pdf". */
  contractNo?: string | null
  onClose: () => void
}

export default function PayDataConfirmPrintDialog({ open, canEdit = true, paydataId, xnttNo, paymentType, contractNo, onClose }: Props) {
  const snack = useSnackbar()
  const [trigger, { data, isFetching, isError, error }] = useLazyPrintPayConfirmQuery()

  useEffect(() => {
    if (open && paydataId != null) {
      void trigger(paydataId)
    }
  }, [open, paydataId, trigger])

  const rawHtml = data?.data?.html ?? ''
  const previewHtml = rawHtml ? buildPrintPreviewHtml(rawHtml, { styleId: 'payconfirm-print-preview-overrides', layout: 'flow' }) : ''
  const iframeTitle = 'Pay Confirm Print Preview'
  const label = xnttNo || (paydataId != null ? String(paydataId) : '')

  useEffect(() => {
    if (open && isError) {
      const detail =
        (error as { data?: { detail?: string } } | undefined)?.data?.detail ?? 'Cannot load Pay Confirm.'
      snack.error(detail)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isError, error])

  return (
    <>
      {open && isFetching && (
        <Box sx={{ position: 'fixed', inset: 0, zIndex: 1300, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(0,0,0,0.35)' }}>
          <CircularProgress size={48} />
        </Box>
      )}
      <Dialog open={open && Boolean(rawHtml)} onClose={onClose} maxWidth={false} fullWidth PaperProps={{ sx: PRINT_PREVIEW_DIALOG_PAPER }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, py: 1 }}>
          <Box component="span" title={`Print Preview - XNTT (${label})`} sx={{ ...TEXT_TRUNCATE, flex: '1 1 auto', minWidth: 0, fontWeight: 700, fontSize: '1rem' }}>
            <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Print Preview - </Box>
            XNTT ({label})
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={onClose} size="small">Cancel</Button>
            {canEdit && (
            <Button
              variant="contained"
              size="small"
              disabled={!rawHtml}
              onClick={() => {
                // Tên file = "<contract no>_<xntt no>_<payment type>.pdf".
                const namePart = [(contractNo ?? '').trim(), label, (paymentType ?? '').trim()].filter(Boolean).join('_')
                const printTitle = `${sanitizePdfFileNamePart(namePart || 'pay_confirm')}.pdf`
                void printAndUploadReport({
                  iframeTitle,
                  reportName: 'rpProcurementPayConfirm',
                  printTitle,
                  html: previewHtml,
                  onUploadError: snack.error,
                })
              }}
            >
              Print
            </Button>
            )}
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ height: '78vh', p: 0 }}>
          <Box component="iframe" title={iframeTitle} srcDoc={previewHtml} sx={{ width: '100%', height: '100%', border: 0, display: 'block', bgcolor: 'white' }} />
        </DialogContent>
      </Dialog>
    </>
  )
}
